import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ChevronDown, LogOut, Moon, Search, Sun } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useTheme } from '@/lib/theme';
import { useAuth } from '@/auth/AuthProvider';
import { BrandMark } from './BrandMark';
import { Sidebar } from './Sidebar';
import { CommandPalette, useCommandPalette } from './CommandPalette';

/**
 * Application shell.
 *
 * A slim top bar with brand, search and the account menu, plus a narrow
 * sidebar — rather than a heavy permanent dashboard chrome. The collapsed
 * state is remembered so the layout does not reset on every navigation.
 */

const COLLAPSE_KEY = 'insider.sidebarCollapsed';

export function AppShell() {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const palette = useCommandPalette();

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, String(collapsed));
    } catch {
      // Storage may be unavailable; the layout still works for this session.
    }
  }, [collapsed]);

  return (
    <div className="flex h-full flex-col">
      <header className="flex h-11 shrink-0 items-center gap-3 border-b border-[var(--color-border)] bg-[var(--color-canvas)] px-3">
        <span className="flex items-center gap-2 text-[13px] font-semibold tracking-tight text-[var(--color-text)]">
          <BrandMark className="h-[17px] w-[17px]" />
          Chișinău Insider
        </span>

        <button
          type="button"
          onClick={() => palette.setOpen(true)}
          className={cn(
            'ml-2 hidden h-7 max-w-[20rem] flex-1 items-center gap-2 rounded-[var(--radius-sm)] border border-[var(--color-border)]',
            'bg-[var(--color-surface-raised)] px-2 text-[12px] text-[var(--color-text-subtle)] transition-colors',
            'hover:border-[var(--color-border-strong)] hover:text-[var(--color-text-muted)] sm:flex',
          )}
        >
          <Search className="h-3.5 w-3.5" />
          <span className="flex-1 text-left">Search…</span>
          <kbd className="rounded border border-[var(--color-border)] px-1 text-[10px]">
            {navigator.platform.toLowerCase().includes('mac') ? '⌘' : 'Ctrl'}K
          </kbd>
        </button>

        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={() => palette.setOpen(true)}
            className="rounded-[var(--radius-sm)] p-1.5 text-[var(--color-text-subtle)] transition-colors hover:bg-[var(--color-hover)] hover:text-[var(--color-text)] sm:hidden"
            aria-label="Search"
          >
            <Search className="h-4 w-4" />
          </button>

          <button
            type="button"
            onClick={toggle}
            className="rounded-[var(--radius-sm)] p-1.5 text-[var(--color-text-subtle)] transition-colors hover:bg-[var(--color-hover)] hover:text-[var(--color-text)]"
            aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          >
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>

          <DropdownMenu.Root>
            <DropdownMenu.Trigger
              className={cn(
                'flex items-center gap-1.5 rounded-[var(--radius-sm)] px-2 py-1 text-[12.5px]',
                'text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-hover)] hover:text-[var(--color-text)]',
              )}
            >
              <span className="max-w-[9rem] truncate">{user?.username ?? 'Account'}</span>
              <ChevronDown className="h-3 w-3" />
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="end"
                sideOffset={6}
                className="z-50 min-w-[13rem] rounded-[var(--radius-md)] border border-[var(--color-border-strong)] bg-[var(--color-canvas)] p-1 shadow-[0_8px_24px_rgba(0,0,0,0.1)]"
              >
                <div className="px-2 py-1.5">
                  <p className="truncate text-[12.5px] font-medium text-[var(--color-text)]">
                    {user?.email}
                  </p>
                  <p className="text-[11.5px] text-[var(--color-text-subtle)]">
                    {user?.role.replace(/_/g, ' ').toLowerCase()}
                  </p>
                </div>
                <DropdownMenu.Separator className="my-1 h-px bg-[var(--color-border)]" />
                <DropdownMenu.Item
                  onSelect={() => void logout()}
                  className="flex cursor-pointer items-center gap-2 rounded-[var(--radius-sm)] px-2 py-1.5 text-[12.5px] text-[var(--color-text)] outline-none data-[highlighted]:bg-[var(--color-hover)]"
                >
                  <LogOut className="h-3.5 w-3.5" />
                  Sign out
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} />
        <main className="min-w-0 flex-1 overflow-y-auto">
          <div className="mx-auto max-w-[1600px] px-6 py-5 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>

      <CommandPalette open={palette.open} onOpenChange={palette.setOpen} />
    </div>
  );
}
